import React, { useCallback, useEffect, useRef, useState } from 'react';
import { formatTimecode } from '../lib/pipeline';
import {
  minGapFor,
  moveEnd,
  moveRegion,
  moveStart,
  nudgeHandle,
  percentOf,
  secondsAtX,
} from '../lib/timeline';

const THUMB_COUNT = 14;
const THUMB_WIDTH = 160;

type Drag = 'start' | 'end' | 'region' | null;

/**
 * Pulls frames with a detached <video> + canvas rather than WebCodecs. Seeking
 * a video element is universally supported and cheap here, and it keeps the
 * scrubber working even in browsers where the WebCodecs path is unavailable.
 */
const useThumbnails = (src: string, durationInSeconds: number) => {
  const [thumbs, setThumbs] = useState<string[]>([]);

  useEffect(() => {
    if (!src || durationInSeconds <= 0) return;
    let cancelled = false;

    const video = document.createElement('video');
    video.src = src;
    video.muted = true;
    video.preload = 'auto';

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    const once = (el: HTMLVideoElement, event: string) =>
      new Promise<void>((resolve, reject) => {
        const ok = () => {
          el.removeEventListener(event, ok);
          el.removeEventListener('error', bad);
          resolve();
        };
        const bad = () => {
          el.removeEventListener(event, ok);
          el.removeEventListener('error', bad);
          reject(new Error(`video ${event} failed`));
        };
        el.addEventListener(event, ok);
        el.addEventListener('error', bad);
      });

    (async () => {
      try {
        await once(video, 'loadedmetadata');
        if (cancelled || !ctx) return;

        const ratio = video.videoHeight / video.videoWidth || 0.5625;
        canvas.width = THUMB_WIDTH;
        canvas.height = Math.round(THUMB_WIDTH * ratio);

        const collected: string[] = [];
        for (let i = 0; i < THUMB_COUNT; i++) {
          if (cancelled) return;
          video.currentTime = ((i + 0.5) / THUMB_COUNT) * durationInSeconds;
          await once(video, 'seeked');
          if (cancelled) return;
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          collected.push(canvas.toDataURL('image/jpeg', 0.55));
          // Publish progressively so the strip fills in rather than popping.
          setThumbs([...collected]);
        }
      } catch {
        // A failed strip is cosmetic — the handles still work over a flat bar.
      }
    })();

    return () => {
      cancelled = true;
      video.removeAttribute('src');
      video.load();
    };
  }, [src, durationInSeconds]);

  return thumbs;
};

export const Filmstrip: React.FC<{
  src: string;
  durationInSeconds: number;
  fps: number;
  start: number;
  end: number;
  playhead: number;
  disabled?: boolean;
  onChange: (start: number, end: number) => void;
  onSeek: (seconds: number) => void;
}> = ({
  src,
  durationInSeconds,
  fps,
  start,
  end,
  playhead,
  disabled = false,
  onChange,
  onSeek,
}) => {
  const thumbs = useThumbnails(src, durationInSeconds);
  const trackRef = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<Drag>(null);
  // Offset between the pointer and the region's start, so region drags don't jump.
  const grabOffset = useRef(0);

  // parseMedia reports null duration for some containers; every position here
  // is a ratio of it, so guard rather than divide by zero.
  const usable = durationInSeconds > 0;
  const pct = (seconds: number) => percentOf(seconds, durationInSeconds);

  /** Pointer x → seconds. The maths lives in lib/timeline.ts and is tested. */
  const secondsAt = useCallback(
    (clientX: number) => {
      const el = trackRef.current;
      if (!el) return 0;
      const box = el.getBoundingClientRect();
      return secondsAtX({
        clientX,
        boxLeft: box.left,
        boxWidth: box.width,
        durationInSeconds,
        fps,
      });
    },
    [durationInSeconds, fps],
  );

  const minGap = minGapFor(fps);

  useEffect(() => {
    if (!drag) return;

    const move = (e: PointerEvent) => {
      const at = secondsAt(e.clientX);
      const current = { start, end };
      const next =
        drag === 'start'
          ? moveStart(current, at, durationInSeconds, minGap)
          : drag === 'end'
            ? moveEnd(current, at, durationInSeconds, minGap)
            : moveRegion(current, at - grabOffset.current, durationInSeconds);
      onChange(next.start, next.end);
    };
    const up = () => setDrag(null);

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };
  }, [drag, start, end, minGap, durationInSeconds, secondsAt, onChange]);

  const nudge = (which: 'start' | 'end', frames: number) => {
    const next = nudgeHandle({ start, end }, which, frames / fps, durationInSeconds, minGap);
    onChange(next.start, next.end);
  };

  if (!usable) {
    return (
      <div className="strip">
        <div className="strip-track">
          <div className="strip-empty">
            No duration reported for this file — trimming unavailable
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`strip ${disabled ? 'is-disabled' : ''}`}>
      <div
        className="strip-track"
        ref={trackRef}
        onPointerDown={(e) => {
          // Clicking bare track seeks; handles stop propagation themselves.
          if (disabled) return;
          onSeek(secondsAt(e.clientX));
        }}
      >
        <div className="strip-thumbs">
          {thumbs.length > 0 ? (
            thumbs.map((t, i) => <img key={i} src={t} alt="" draggable={false} />)
          ) : (
            <div className="strip-empty">reading frames…</div>
          )}
        </div>

        {/* Excluded regions */}
        <div className="strip-mask" style={{ left: 0, width: `${pct(start)}%` }} />
        <div
          className="strip-mask"
          style={{ left: `${pct(end)}%`, width: `${100 - pct(end)}%` }}
        />

        <div
          className="strip-selection"
          style={{ left: `${pct(start)}%`, width: `${pct(end - start)}%` }}
          onPointerDown={(e) => {
            if (disabled) return;
            e.stopPropagation();
            grabOffset.current = secondsAt(e.clientX) - start;
            setDrag('region');
          }}
        />

        <div className="strip-playhead" style={{ left: `${pct(playhead)}%` }} />

        {(['start', 'end'] as const).map((which) => (
          <div
            key={which}
            className={`strip-handle ${which} ${drag === which ? 'active' : ''}`}
            style={{ left: `${pct(which === 'start' ? start : end)}%` }}
            role="slider"
            tabIndex={disabled ? -1 : 0}
            aria-label={`${which} of range`}
            aria-valuemin={0}
            aria-valuemax={durationInSeconds}
            aria-valuenow={which === 'start' ? start : end}
            aria-valuetext={formatTimecode((which === 'start' ? start : end) * 1000)}
            onPointerDown={(e) => {
              if (disabled) return;
              e.stopPropagation();
              setDrag(which);
            }}
            onKeyDown={(e) => {
              if (disabled) return;
              const step = e.shiftKey ? fps : 1;
              if (e.key === 'ArrowLeft') {
                e.preventDefault();
                nudge(which, -step);
              } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                nudge(which, step);
              }
            }}
          >
            <span className="strip-grip" />
          </div>
        ))}
      </div>

      <div className="strip-readout mono">
        <span>in {formatTimecode(start * 1000)}</span>
        <span className="dim">
          {(end - start).toFixed(1)}s selected of {durationInSeconds.toFixed(1)}s
        </span>
        <span>out {formatTimecode(end * 1000)}</span>
      </div>
    </div>
  );
};
